import type { HealthScore } from "@/lib/api";

const CATEGORY_STYLES: Record<HealthScore["status"], { ring: string; text: string; bg: string; emoji: string }> = {
  Excellent: { ring: "stroke-green-600", text: "text-green-700", bg: "bg-green-50", emoji: "🟢" },
  Healthy: { ring: "stroke-green-500", text: "text-green-600", bg: "bg-green-50", emoji: "🟢" },
  Monitor: { ring: "stroke-yellow-500", text: "text-yellow-700", bg: "bg-yellow-50", emoji: "🟡" },
  "At Risk": { ring: "stroke-orange-500", text: "text-orange-700", bg: "bg-orange-50", emoji: "🟠" },
  Critical: { ring: "stroke-red-500", text: "text-red-700", bg: "bg-red-50", emoji: "🔴" },
};

export default function HealthScoreCard({ healthScore }: { healthScore: HealthScore }) {
  const { score, status, reasons } = healthScore;
  const style = CATEGORY_STYLES[status];

  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - score / 100);

  return (
    <div className="bg-white rounded-lg border border-background-200 p-4 md:p-5 mb-6">
      <h2 className="text-primary-600 text-base font-semibold mb-4">Contract Health Score</h2>
      <div className="flex items-center gap-6 flex-wrap">
        <div className="relative w-28 h-28 shrink-0">
          <svg viewBox="0 0 100 100" className="w-28 h-28 -rotate-90">
            <circle cx="50" cy="50" r={radius} fill="none" strokeWidth="8" className="stroke-background-200" />
            <circle
              cx="50"
              cy="50"
              r={radius}
              fill="none"
              strokeWidth="8"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              strokeLinecap="round"
              className={`${style.ring} transition-all duration-500`}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-foreground-900 text-2xl font-bold">{score}</span>
            <span className="text-foreground-400 text-[10px]">/ 100</span>
          </div>
        </div>

        <div className="flex-1 min-w-[180px]">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${style.bg} ${style.text}`}>
            <span>{style.emoji}</span>{status}
          </span>

          {reasons.length > 0 ? (
            <ul className="mt-3 space-y-1.5">
              {reasons.map((reason) => (
                <li key={reason} className="flex items-start gap-2 text-foreground-600 text-sm">
                  <i className="ri-subtract-line text-foreground-300 text-sm mt-0.5"></i>
                  {reason}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-3 text-foreground-400 text-sm">No issues detected.</p>
          )}
        </div>
      </div>
    </div>
  );
}
